import type { ReactNode } from 'react';
import {
  BedIcon,
  ClockCounterClockwiseIcon,
  HandshakeIcon,
  HeartIcon,
  SparkleIcon,
  TagIcon,
  TimerIcon,
  type Icon,
} from '@phosphor-icons/react';
import { Badge } from '@voll-intelligence/ui-kit/ui/badge';
import { cn } from '@voll-intelligence/ui-kit/utils';

export type OfferPolicyTone = 'within' | 'outside' | 'unknown';

/** O EIXO do selo, e é ele que escolhe o glifo — o Sparkle no recomendado
 *  (ali existe autoria: é a escolha do agente), etiqueta no menor preço, cronômetro no mais perto, a
 *  cama no selo pessoal `stayed` ("você ficou aqui": o relógio fica só com a
 *  busca recente, #3990) e o coração no selo pessoal `favorite` (escolha
 *  declarada do viajante — acima até da estadia). `lastBooked` is the car's
 *  personal stamp ("Última reserva", #5529): the offer that repeats the
 *  traveler's last rental, drawn with a counter-clockwise clock. `agreement`
 *  is the hotel's "Hotel com acordo" (#6359), drawn with a handshake.
 *  Campo próprio e não o `id` porque `id` é a chave da lista: quem produz pode
 *  precisar de ids únicos, e o desenho não pode depender do formato deles. */
export type OfferBadgeIcon =
  | 'recommended'
  | 'cheapest'
  | 'closest'
  | 'stayed'
  | 'favorite'
  | 'lastBooked'
  | 'agreement';

export type OfferBadgeView = {
  id: string;
  label: string;
  tone: OfferPolicyTone;
  icon: OfferBadgeIcon;
};

/** O selo do eixo, na gramática do protótipo (`SeloDoEixo`): o recomendado é
 *  SÓLIDO — é ele que faz parar no card — e o resto é `soft`; fora-da-política
 *  herda o vermelho do quarteto semântico. */
const badgeVariant = (tone: OfferPolicyTone) =>
  tone === 'within'
    ? ({ variant: 'solid', color: 'neutral' } as const)
    : ({ variant: 'soft', color: tone === 'outside' ? 'danger' : 'neutral' } as const);

/** A tinta do selo sólido (o Recomendado) em TODAS as superfícies, pedido do
 *  design em 29/09/2026 (#3874): `--muted-foreground` sobre `--background`, e
 *  não o ink cheio que o `solid neutral` do átomo pinta. Fica aqui e não no
 *  átomo porque é a cor DESTE selo — o `solid neutral` segue ink para quem
 *  mais o usar. O mesmo par vive no card de voo (`.fq-flag`) e na tela de
 *  tarifas. */
const RECOMMENDED_INK = 'bg-muted-foreground text-background';

/** The glyph of every axis but the recommended one (filled Sparkle, below).
 *  The PERSONAL stamps: `stayed` ("você ficou aqui") is the bed — the
 *  traveler's stay, not an inventory fact; the clock belongs to the recent
 *  search and is not repeated there (#3990). `favorite` is the heart — the
 *  choice the traveler declared. `lastBooked` is the counter-clockwise clock —
 *  the car the traveler rented last (#5529). */
const FACT_GLYPHS: Record<Exclude<OfferBadgeIcon, 'recommended'>, Icon> = {
  cheapest: TagIcon,
  closest: TimerIcon,
  stayed: BedIcon,
  favorite: HeartIcon,
  lastBooked: ClockCounterClockwiseIcon,
  agreement: HandshakeIcon,
};

/** O GLIFO do eixo, na gramática do protótipo (`GLIFO_DO_EIXO`): o Sparkle
 *  preenchido no recomendado, porque ali existe AUTORIA — é a escolha do
 *  agente, o mesmo glifo da nota da IA. Já foi o V da VOLL, que o cliente lia
 *  como recomendação ou tarifa DA VOLL (#4274). Os outros são fatos conferíveis
 *  no dado, e ganham etiqueta e cronômetro. */
function BadgeGlyph({ icon }: { readonly icon: OfferBadgeIcon }): ReactNode {
  if (icon === 'recommended') return <SparkleIcon aria-hidden="true" weight="fill" />;
  const Glyph = FACT_GLYPHS[icon];
  return <Glyph aria-hidden="true" />;
}

/**
 * Um selo de eixo sobre o card de oferta (hotel, carro): glifo + rótulo, na
 * variante que o tom pede. `ring` é o anel na cor do card, que existe para o
 * selo INTERROMPER a borda do card em vez de boiar em cima; sobre o mapa não
 * há borda que interromper e o protótipo desliga o anel.
 */
export function OfferBadge({
  badge,
  ring = true,
}: {
  readonly badge: OfferBadgeView;
  readonly ring?: boolean;
}): ReactNode {
  return (
    <Badge
      {...badgeVariant(badge.tone)}
      className={cn(badge.tone === 'within' && RECOMMENDED_INK, ring && 'ring-card ring-2')}
    >
      <BadgeGlyph icon={badge.icon} />
      {badge.label}
    </Badge>
  );
}
